import React, { useState } from 'react';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { FaPlus, FaCheck, FaEdit, FaTrash } from 'react-icons/fa';
import { format } from 'date-fns';
import { Notification } from '../../../stateManagement/SettingsContext';
import { CustomButton, CustomInput, CustomText } from '../../atoms';
import { ToggleAtom } from '../../atoms/CustomToggle';
import { ColorPickerAtom } from '../../atoms/ColorPickerAtom';
import { DeleteConfirmation } from './DeletionConfirmationMolecule';

interface NotificationSettingsProps {
  notifications: Notification[];
  onNotificationChange: (notifications: Notification[]) => void;
  configs: {
    showNotificationBanner: boolean;
    notificationBannerBgColor: string;
    notificationBannerTextColor: string;
  };
  onConfigChange: (key: string, value: any) => void;
}

export const NotificationSettingsMolecule: React.FC<NotificationSettingsProps> = ({
  notifications,
  onNotificationChange,
  configs,
  onConfigChange,
}) => {
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [currentEdit, setCurrentEdit] = useState<Notification | null>(null);
  const [showNotificationBanner, setShowNotificationBanner] = useState(
    configs.showNotificationBanner,
  );

  const handleEditNotification = (notification: Notification) => {
    setEditingCardId(notification.id);
    setCurrentEdit({ ...notification });
  };

  // Function to handle saving the edits
  const handleSaveEdit = () => {
    if (currentEdit && editingCardId) {
      const updatedNotifs = notifications.map(card =>
        card.id === editingCardId ? currentEdit : card,
      );
      onNotificationChange(updatedNotifs);
      setEditingCardId(null);
      setCurrentEdit(null);
    }
  };

  // Function to handle deleting a notification
  const handleDeleteNotification = (id: string) => {
    const updatedCards = notifications.filter(card => card.id !== id);
    onNotificationChange(updatedCards);
  };

  // Function to update the title in the input field
  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (currentEdit) {
      setCurrentEdit({ ...currentEdit, message: event.target.value });
    }
  };
  const handleStartTimeChange = (date: Date | null) => {
    if (currentEdit) {
      setCurrentEdit({
        ...currentEdit,
        startTime: date ? date.toISOString() : '', // Or just store Date object if preferred
      });
    }
  };

  const handleEndTimeChange = (date: Date | null) => {
    if (currentEdit) {
      setCurrentEdit({
        ...currentEdit,
        endTime: date ? date.toISOString() : '', // Or just store Date object if preferred
      });
    }
  };
  // Function to add a new news card at the top of the list
  const handleAddNotification = () => {
    const newNotif: Notification = {
      id: Date.now().toString(),
      message: '',
      startTime: '',
      endTime: '',
    };
    onNotificationChange([newNotif, ...notifications]);
    setEditingCardId(newNotif.id);
    setCurrentEdit(newNotif);
  };

  return (
    <Box height="80vh" overflowY="auto" border="1px solid #e2e8f0" p={4} borderRadius="md">
      <HStack
        p={4}
        borderWidth="1px"
        borderRadius="md"
        mb={4}
        justifyContent="space-between"
        alignItems="center"
      >
        <ToggleAtom
          justifyContent="space-between"
          label="Show Notifications"
          value={showNotificationBanner}
          onChange={value => {
            setShowNotificationBanner(value); // Update local state
            onConfigChange('showNotificationBanner', value); // Notify parent of the change
          }}
        />
      </HStack>
      <HStack
        p={4}
        borderWidth="1px"
        borderRadius="md"
        mb={4}
        justifyContent="space-between"
        alignItems="center"
      >
        <ColorPickerAtom
          label="Notification Background Color"
          value={configs.notificationBannerBgColor}
          onChange={color => onConfigChange('notificationBannerBgColor', color)}
        />
      </HStack>
      <HStack
        p={4}
        borderWidth="1px"
        borderRadius="md"
        mb={4}
        justifyContent="space-between"
        alignItems="center"
      >
        <ColorPickerAtom
          label="Notification Text Color"
          value={configs.notificationBannerTextColor}
          onChange={color => onConfigChange('notificationBannerTextColor', color)}
        />
      </HStack>
      <CustomButton
        icon={<FaPlus />}
        // aria-label=""
        onClick={handleAddNotification}
        colorScheme="green"
      >
        Add Notification
      </CustomButton>

      <Box maxWidth="100%" overflow="hidden">
        <Stack spacing={4}>
          <Box mt={6}>
            {notifications.map(card => (
              <Box
                key={card.id}
                p={4}
                borderWidth="1px"
                borderRadius="md"
                mb={4}
                display="flex"
                flexDirection="column"
                gap={4} // Adds spacing between elements
              >
                {/* Edit Mode */}
                {editingCardId === card.id ? (
                  <Stack spacing={3} width="100%">
                    <CustomInput
                      value={currentEdit?.message || ''}
                      onChange={handleMessageChange}
                      placeholder="Message"
                      width="100%"
                    />
                    <CustomInput
                      value={currentEdit?.startTime || ''}
                      dateTimeValue={
                        currentEdit?.startTime ? new Date(currentEdit.startTime) : null
                      }
                      onDateTimeChange={handleStartTimeChange}
                      placeholder="Start Time"
                      type="date-time"
                      width="100%"
                    />
                    <CustomInput
                      value={currentEdit?.endTime || ''}
                      //   onChange={handleEndTimeChange}
                      onDateTimeChange={handleEndTimeChange}
                      dateTimeValue={currentEdit?.endTime ? new Date(currentEdit.endTime) : null}
                      placeholder="End Time"
                      type="date-time"
                      width="100%"
                    />
                  </Stack>
                ) : (
                  // Display Mode: Stack all text elements with wrapping enabled
                  <Stack spacing={3} width="100%">
                    <CustomText fontWeight="bold" whiteSpace="normal">
                      {card.message}
                    </CustomText>
                    {/* <CustomText whiteSpace="normal">{card.startTime}</CustomText>
                    <CustomText whiteSpace="normal">{card.endTime}</CustomText> */}
                    <CustomText whiteSpace="normal">
                      {card.startTime
                        ? format(new Date(card.startTime), 'dd MMM yyyy, hh:mm a')
                        : '--'}
                    </CustomText>
                    <CustomText whiteSpace="normal">
                      {card.endTime ? format(new Date(card.endTime), 'dd MMM yyyy, hh:mm a') : '--'}
                    </CustomText>
                  </Stack>
                )}

                {/* Move Buttons to a New Line */}
                <HStack justifyContent="flex-start" flexWrap="wrap" gap={2}>
                  {editingCardId === card.id ? (
                    <CustomButton
                      icon={<FaCheck />}
                      aria-label="Save Changes"
                      onClick={handleSaveEdit}
                      colorScheme="green"
                    />
                  ) : (
                    <CustomButton
                      icon={<FaEdit />}
                      aria-label="Edit Notification"
                      onClick={() => handleEditNotification(card)}
                      colorScheme="yellow"
                    />
                  )}
                  <DeleteConfirmation
                    message="Are you sure you want to delete this notification card?"
                    onConfirm={() => handleDeleteNotification(card.id)}
                    trigger={
                      <CustomButton
                        icon={<FaTrash />}
                        aria-label="Delete Notification"
                        colorScheme="red"
                      />
                    }
                  />

                  {/* <CustomButton
                    icon={<FaTrash />}
                    aria-label="Delete Notification"
                    colorScheme="red"
                    onClick={() => handleDeleteNotification(card.id)} // show confirmation modal
                  /> */}
                </HStack>
              </Box>
            ))}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};
